import type { Automation } from "@repo/app-types/entities/automation";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Switch } from "@repo/ui/Switch/switch";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { FC } from "react";
import type { AutosaveStatus } from "../../hooks/useAutosaveDraft.js";
import { AutomationInfoPopover } from "./automationInfoPopover.js";

export interface AutomationDetailActionsProps {
  automation: Automation;
  saveStatus?: AutosaveStatus;
  lastSavedAt?: string | null;
  createdByName?: string;
  /**
   * Renders Delete / Run preview / the enable toggle disabled for viewers who
   * cannot modify agents (reviewers).
   */
  isReadOnly?: boolean;
  onToggleEnabled: (isEnabled: boolean) => void;
  onDeleteRequest?: () => void;
  onRunPreview?: () => void;
  isRunPreviewLoading?: boolean;
}

/**
 * The agent detail page header's actions: the Delete / Run preview / enable
 * controls and the info popover (save state, next run, created).
 * `onDeleteRequest` and `onRunPreview` are optional; when omitted those
 * actions are hidden.
 */
export const AutomationDetailActions: FC<AutomationDetailActionsProps> = (
  props,
) => {
  const {
    automation,
    saveStatus = "idle",
    lastSavedAt = null,
    createdByName,
    isReadOnly,
    onToggleEnabled,
    onDeleteRequest,
    onRunPreview,
    isRunPreviewLoading,
  } = props;
  const { is_enabled: isEnabled } = automation;

  return (
    <>
      {onDeleteRequest ? (
        <Button
          variant={ButtonVariant.Warning}
          size={Size.SMALL}
          isDisabled={isReadOnly}
          onClick={onDeleteRequest}
        >
          Delete
        </Button>
      ) : null}
      {onRunPreview ? (
        <TooltipTrigger>
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.SMALL}
            isLoading={isRunPreviewLoading}
            isDisabled={isReadOnly}
            onClick={onRunPreview}
          >
            Run preview
          </Button>
          <SimpleTooltip>
            Runs this agent without sending any notifications. The run and its
            results appear in the Results tab.
          </SimpleTooltip>
        </TooltipTrigger>
      ) : null}
      <Switch
        isSelected={isEnabled}
        isDisabled={isReadOnly}
        onChange={onToggleEnabled}
        accessibleLabel={isEnabled ? "Disable agent" : "Enable agent"}
      />
      <AutomationInfoPopover
        automation={automation}
        saveStatus={saveStatus}
        lastSavedAt={lastSavedAt}
        createdByName={createdByName}
      />
    </>
  );
};
